// Disclaimer.jsx
import React from "react";
import { motion } from "framer-motion";
import "./Disclaimer.css";
import { useLocalContext } from "../../context/LocalContext";
import {
  resolveSiteEmail,
  resolveSiteName,
  resolveSitePhone,
} from "../../utils/siteConfig";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();

  const company = resolveSiteName(webinfo);
  const email = resolveSiteEmail(webinfo);
  const phone = resolveSitePhone(webinfo);
  const phoneCall = String(webinfo?.phonecall || "").trim();

  const items = [
    {
      icon: "i",
      title: "General information",
      text: (
        <>
          The information published on this website by <strong>{company}</strong>{" "}
          is provided for general informational purposes only. We try to keep
          content accurate and up to date, but we make no warranties of any kind
          about completeness, reliability, or accuracy.
        </>
      ),
    },
    {
      icon: "!",
      title: "No professional advice",
      text: (
        <>
          Nothing on this site should be considered legal, financial, medical,
          or professional advice. Always consult qualified professionals for
          guidance specific to your situation.
        </>
      ),
    },
    {
      icon: "✓",
      title: "Use at your own risk",
      text: (
        <>
          Any action you take based on this website is strictly at your own
          risk. <strong>{company}</strong> will not be liable for losses or
          damages arising from use of the site, services, or materials.
        </>
      ),
    },
    {
      icon: "↗",
      title: "External links",
      text: (
        <>
          This website may contain links to third-party websites. We do not
          control their content and do not necessarily endorse it. Visiting
          external links is your choice and responsibility.
        </>
      ),
    },
    {
      icon: "∞",
      title: "Service outcomes",
      text: (
        <>
          Results can vary due to factors like market conditions, timelines,
          existing assets, and client collaboration. We do not guarantee
          specific rankings, revenue, or performance outcomes.
        </>
      ),
    },
    {
      icon: "⟳",
      title: "Updates & changes",
      text: (
        <>
          We may update, modify, or remove content at any time without prior
          notice. Continued use of the website means you accept the latest
          version of this disclaimer.
        </>
      ),
    },
  ];

  return (
    <section className="disc" aria-label="Disclaimer">
      <div className="disc__wrap">
        {/* HERO */}
        <motion.div
          className="disc__hero"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.45 }}
        >
          <div className="disc__badge">
            <span className="dot" aria-hidden="true" />
            Legal notice
          </div>

          <h1 className="disc__title">
            Disclaimer for <span>{company}</span>
          </h1>

          <p className="disc__sub">
            This page explains important limitations related to the information
            and services available on our website. Please read it carefully.
          </p>
        </motion.div>

        {/* CONTENT */}
        <div className="disc__grid">
          {items.map((it, idx) => (
            <motion.div
              key={idx}
              className="disc__card"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <i className="disc__icon" aria-hidden="true">
                {it.icon}
              </i>

              <div>
                <h3 className="disc__h3">{it.title}</h3>
                <p className="disc__p">{it.text}</p>
              </div>
            </motion.div>
          ))}

          {/* CONTACT NOTICE */}
          <motion.div
            className="disc__card disc__card--notice"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <i className="disc__icon" aria-hidden="true">
              ✉
            </i>

            <div>
              <h3 className="disc__h3">Questions?</h3>
              <p className="disc__p">
                If you have questions about this disclaimer, contact us at{" "}
                <a href={`mailto:${email}`}>{email}</a>
                {phone ? (
                  <>
                    {" "}
                    or call <strong>{phone}</strong>.
                  </>
                ) : null}
              </p>

              <div className="disc__actions">
                <a className="disc__btn disc__btn--primary" href={`mailto:${email}`}>
                  Email us <span aria-hidden="true">→</span>
                </a>

                {phoneCall ? (
                  <a className="disc__btn disc__btn--ghost" href={`tel:${phoneCall}`}>
                    Call now
                  </a>
                ) : (
                  <a className="disc__btn disc__btn--ghost" href="/contact">
                    Contact page
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Disclaimer;